import { Upload } from "@aws-sdk/lib-storage";
import { GetObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { base64ToBlob, blobToBase64, getRandomString } from "../utils/functions";

const PROJECT_ID = process.env.REACT_APP_PROJECT_ID as string;
const FUNCTION_URL = `https://api.tranfoo.com/${PROJECT_ID}/function`;
const dateRetrieverId = process.env.REACT_APP_FUNCTION_ID_DATA_RETRIEVER as string;
const dataWriterId = process.env.REACT_APP_FUNCTION_ID_DATA_WRITER as string;
const authHandlerId = process.env.REACT_APP_FUNCTION_ID_AUTH_HANDLER as string;
const storageHandlerId = process.env.REACT_APP_FUNCTION_ID_STORAGE_HANDLER as string;
const adminAuthHandlerId = process.env.REACT_APP_FUNCTION_ID_ADMIN_AUTH_HANDLER as string;
const adminDataHandlerId = process.env.REACT_APP_FUNCTION_ID_ADMIN_DATA_HANDLER as string;
const adminDatabaseManagerId = process.env.REACT_APP_FUNCTION_ID_ADMIN_DATABASE_MANAGER as string;
const adminStorageHandlerId = process.env.REACT_APP_FUNCTION_ID_ADMIN_STORAGE_HANDLER as string;
const adminUtilHandlerId = process.env.REACT_APP_FUNCTION_ID_ADMIN_UTIL_HANDLER as string;

// using s3 directly
const bucketName = 'tranfoogeneralbucket';
const ACCESS_KEY_ID = process.env.REACT_APP_AWS_ACCESS_KEY_ID as string;
const ACCESS_KEY_SECRET = process.env.REACT_APP_AWS_ACCESS_KEY_SECRET as string;
const s3 = new S3Client({
  region: 'ap-northeast-1',
  credentials: {
    accessKeyId: ACCESS_KEY_ID,
    secretAccessKey: ACCESS_KEY_SECRET,
  },
});

// DATA ACCESS

// get single item from database
export const getItem = async (table: string, id: string) => {
  let error;
  try {
    const data = await (await fetch(`${FUNCTION_URL}/${dateRetrieverId}?t=${table}&tid=${id}`)).json();
    return Object.keys(data).length > 0 ? { item: data } : { error: 'Item not found' };
  } catch (err) {
    error = err;
  }
  return { error };
}

// get multiple items from database
export const getItems = async (table: string, filters?: any, limit?: number, offset?: number) => {
  let error;
  try {
    for (const key in filters) {
      if (!filters[key]) filters[key] = '';
    }
    const response = filters && Object.keys(filters).length > 0 ? 
      await fetch(`${FUNCTION_URL}/${dateRetrieverId}?t=${table}&c=${encodeURIComponent(JSON.stringify(filters))}&limit=${limit || 100}&offset=${offset || 0}`) : 
      await fetch(`${FUNCTION_URL}/${dateRetrieverId}?t=${table}&limit=${limit || 100}&offset=${offset || 0}`);
    const data = await response.json();
    return { items: data };
  } catch (err) {
    error = err;
  }
  return { error };
}

// delete item from database
export const deleteItems = async (table: string, ids: string[]) => {
  let error;
  try {
    await fetch(`${FUNCTION_URL}/${dataWriterId}?t=${table}&ids=${JSON.stringify(ids)}`, { method: 'DELETE' });
    return { success: true };
  } catch (err) {
    error = err;
  }
  return { error };
}

// put an item into the database
export const createItem = async (table: string, data: any) => {
  let error;
  try {
    await fetch(`${FUNCTION_URL}/${dataWriterId}?t=${table}`, { method: 'POST', body: JSON.stringify(data), headers: { 'Content-Type': 'application/json' } });
    return { success: true };
  } catch (err) {
    error = err;
  }
  return { error };
}

// update an item in the database
export const updateItem = async (table: string, id: string, changes: Record<string, any>) => {
  let error;
  try {
    await fetch(`${FUNCTION_URL}/${dataWriterId}?t=${table}&tid=${id}`, { method: 'PUT', body: JSON.stringify(changes), headers: { 'Content-Type': 'application/json' } });
    return { success: true };
  } catch (err) {
    error = err;
  }
  return { error };
}


// AUTH HANDLING

// sign in
export const userSignIn = async (username: string, password: string) => {
  let error;
  try {
    await fetch(`${FUNCTION_URL}/${authHandlerId}?ac=1`, { method: 'POST', body: JSON.stringify({ username, password }), headers: { 'Content-Type': 'application/json' } });
    return { success: true };
  } catch (err) {
    error = err;
  }
  return { error };
}

// sign up
export const userSignUp = async (username: string, password: string, email: string) => {
  let error;
  try {
    await fetch(`${FUNCTION_URL}/${authHandlerId}?ac=2`, { method: 'POST', body: JSON.stringify({ username, password, email }), headers: { 'Content-Type': 'application/json' } });
    return { success: true };
  } catch (err) {
    error = err;
  }
  return { error };
}

// confirm sign up
export const userConfirmSignUp = async (username: string, code: string) => {
  let error;
  try {
    await fetch(`${FUNCTION_URL}/${authHandlerId}?ac=3`, { method: 'POST', body: JSON.stringify({ username, code }), headers: { 'Content-Type': 'application/json' } });
    return { success: true };
  } catch (err) {
    error = err;
  }
  return { error };
}


// STORAGE

// upload file
export const uploadFile = async (storageName: string, fileName: string, file: File) => {
  let error;
  try {
    const res = await fetch(`${FUNCTION_URL}/${storageHandlerId}?st=${storageName}&f=${fileName}&ac=put&c=${file.type}`, { headers: { 'Content-Type': 'application/json' } });
    const { url } = await res.json();
    await fetch(url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
    return { success: true };
  } catch (err) {
    error = err;
  }
  return { error };
}

// get file content
export const getFileContent = async (storageName: string, fileName: string) => {
  let error;
  try {
    const res = await fetch(`${FUNCTION_URL}/${storageHandlerId}?st=${storageName}&f=${fileName}&ac=get`, { headers: { 'Content-Type': 'application/json' } });
    const { url } = await res.json();
    const blob = await fetch(url).then(res => res.blob());
    return { blob };
  } catch (err) {
    error = err;
  }
  return { error };
}

// delete file
export const deleteFile = async (storageName: string, fileName: string) => {
  let error;
  try {
    await fetch(`${FUNCTION_URL}/${storageHandlerId}?st=${storageName}&f=${fileName}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' } });
    return { success: true };
  } catch (err) {
    error = err;
  }
  return { error };
}


/// ADMIN ///


export const Admin = {
  // DATA
  getItem: async (table: string, id: string) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminDataHandlerId}?t=${table}&id=${id}`, { headers: { 'Content-Type': 'application/json' } });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      const data = await response.json();
      return Object.keys(data).length > 0 ? { item: data } : { error: 'Item not found' };
    } catch (err) {
      error = err;
    }
    return { error };
  },
  getItems: async (table: string, filters?: object, limit?: number, offset?: number) => {
    let error;
    try {
      const apiString = filters && Object.keys(filters).length ? 
        `${FUNCTION_URL}/${adminDataHandlerId}?t=${table}&c=${encodeURIComponent(JSON.stringify(filters))}&limit=${limit || 100}&offset=${offset || 0}` : 
        `${FUNCTION_URL}/${adminDataHandlerId}?t=${table}&limit=${limit || 100}&offset=${offset || 0}`;
      const response = await fetch(apiString, { headers: { 'Content-Type': 'application/json' } });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { items: await response.json() };
    } catch (err) {
      error = err;
    }
    return { error };
  },
  createItem: async (table: string, data: any) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminDataHandlerId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ table_name: table, item: data }) });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { success: true };
    } catch (err) {
      error = err;
    }
    return { error };
  },
  createItems: async (table: string, data: any[]) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminDataHandlerId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ table_name: table, items: data }) });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { success: true };
    } catch (err) {
      error = err;
    }
    return { error };
  },
  updateItem: async (table: string, id: string, changes: Record<string, any>) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminDataHandlerId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ table_name: table, id, changes }) });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { success: true };
    } catch (err) {
      error = err;
    }
    return { error };
  },
  deleteItems: async (table: string, ids: string[]) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminDataHandlerId}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ table_name: table, ids }) });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { success: true };
    } catch (err) {
      error = err;
    }
    return { error };
  },
  getDatabaseMetadata: async (table: string) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminDataHandlerId}?t=${table}&type=metadata`, { headers: { 'Content-Type': 'application/json' } });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { metadata: (await response.json()).metadata };
    } catch (err) {
      error = err;
    }
    return { error };
  },

  // DATABASES
  createDatabase: async (name: string) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminDatabaseManagerId}?t=${name}`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { success: true };
    } catch (err) {
      error = err;
    }
    return { error };
  },
  deleteDatabase: async (name: string) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminDatabaseManagerId}?t=${name}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' } });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { success: true };
    } catch (err) {
      error = err;
    }
    return { error };
  },

  // AUTH
  createUserPool: async (poolName: string, clientName: string, minLength: number, requireNumbers: boolean, requireSymbols: boolean, requireUppercase: boolean, requireLowercase: boolean, emailSubject: string, emailMessage: string) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminAuthHandlerId}?ac=p-add`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({
          poolName,
          clientName,
          policies: {
            "PasswordPolicy": {
              "MinimumLength": minLength,
              "RequireUppercase": requireUppercase,
              "RequireLowercase": requireLowercase,
              "RequireNumbers": requireNumbers,
              "RequireSymbols": requireSymbols,
              "TemporaryPasswordValidityDays": 7
            }
          },
          verificationMessageTemplate: {
            'DefaultEmailOption': 'CONFIRM_WITH_CODE',
            'EmailSubject': emailSubject,
            'EmailMessage': emailMessage
          }
        })
      });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return await response.json();
    } catch (err) {
      error = err;
    }
    return { error };
  },
  deleteUserPool: async (userPoolId: string) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminAuthHandlerId}?ac=p-del&pid=${userPoolId}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' } });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { success: true };
    } catch (err) {
      error = err;
    }
    return { error };
  },
  updatePasswordRequirements: async (userPoolId: string, minLength: number, requireNumber: boolean, requireSymbol: boolean, requireUppercase: boolean, requireLowercase: boolean) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminAuthHandlerId}?ac=pr-upd&pid=${userPoolId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ minLength, requireNumber, requireSymbol, requireUppercase, requireLowercase }) });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { success: true };
    } catch (err) {
      error = err;
    }
    return { error };
  },
  updateEmailTemplate: async (userPoolId: string, option: 'CONFIRM_WITH_CODE' | 'CONFIRM_WITH_LINK', subject: string, message: string) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminAuthHandlerId}?ac=t-upd&pid=${userPoolId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ option, subject, message }) });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { success: true };
    } catch (err) {
      error = err;
    }
    return { error };
  },
  retrieveUsers: async (userPoolId: string) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminAuthHandlerId}?ac=u-get&pid=${userPoolId}`, { headers: { 'Content-Type': 'application/json' } });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return await response.json();
    } catch (err) {
      error = err;
    }
    return { error };
  },
  deleteUser: async (userPoolId: string, username: string) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminAuthHandlerId}?ac=u-del&pid=${userPoolId}&u=${username}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' } });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { success: true };
    } catch (err) {
      error = err;
    }
    return { error };
  },
  addUser: async (userPoolId: string, { username, email, password }: { username: string; email: string; password: string }) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminAuthHandlerId}?ac=u-add&pid=${userPoolId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username, email, password }) });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { success: true };
    } catch (err) {
      error = err;
    }
    return { error };
  },

  // STORAGE
  uploadLargeFile: async (filename: string, contentType: string, file: File) => {
    let error;
    try {
      // Use multipart upload for large files
      const upload = new Upload({ client: s3, params: {
        Bucket: bucketName,
        Key: filename,
        Body: file,
        ContentType: contentType,
      }});
      await upload.done();
      return { success: true };
    } catch (err) {
      error = err;
    }
    return { error };
  },
  deleteFile: async (filename: string) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminStorageHandlerId}?f=${filename}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' } });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { success: true };
    } catch (err) {
      error = err;
    }
    return { error };
  },
  downloadFile: async (filename: string) => {
    let error;
    try {
      const command = new GetObjectCommand({
        Bucket: bucketName,
        Key: filename,
      });
      const response = await s3.send(command);
      // Convert the stream into a Blob for downloading
      const blob = await response.Body?.transformToByteArray();
      if (!blob) return { error: 'File not found' };
      const url = URL.createObjectURL(new Blob([blob]));
  
      // Create a link element to trigger a download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename.split("/").pop() || 'download.txt');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      error = err;
    }
    return { error };
  },

  // UTILS
  // call a test function
  testFunction: async (projectId: string, functionId: string, httpMethod: string, queryParams: string, headers: { name: string, value: string }[], body: string) => {
    const additionalHeaders = headers.reduce((acc, header) => {
      acc[header.name] = header.value;
      return acc;
    }, {} as Record<string, string>);
    try {
      const queryString = queryParams.split('&').map(pair => {
        let [key, value] = pair.split('=');
        return `${key}=${encodeURIComponent(value)}`;
      }).join('&');
      const response = await fetch(`https://api.tranfoo.com/tmp_${getRandomString(10)}/func-test?project_id=${projectId}&function_id=${functionId}&${queryString}`, {
        method: httpMethod, headers: { 'Content-Type': 'application/json', ...additionalHeaders }, body: httpMethod === 'GET' ? undefined : body
      });
      if (response.status !== 200) {
        return { statusCode: response.status, body: (await response.text()) };
      }
      return { statusCode: response.status, body: (await response.json()) };
    } catch (err) {
      return { statusCode: 500, body: 'Internal Server Error' };
    }
  },

  // get api key
  getApiKey: async (projectId: string) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminUtilHandlerId}?ac=ak-get&pid=${projectId}`, { headers: { 'Content-Type': 'application/json' } });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return await response.json();
    } catch (err) {
      error = err;
    }
    return { error };
  },

  // create api key
  createApiKey: async (projectId: string) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminUtilHandlerId}?ac=ak-add&pid=${projectId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return await response.json();
    } catch (err) {
      error = err;
    }
    return { error };
  },

  // delete api key
  deleteApiKey: async (projectId: string) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminUtilHandlerId}?ac=ak-del&pid=${projectId}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' } });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { success: true };
    } catch (err) {
      error = err;
    }
    return { error };
  },

  // get function environment variables
  getFunctionEnvs: async (projectId: string, functionId: string) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminUtilHandlerId}?ac=env-get&pid=${projectId}&fid=${functionId}`, { headers: { 'Content-Type': 'application/json' } });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return (await response.json()).env;
    } catch (err) {
      error = err;
    }
    return { error };
  },

  // set function environment variables
  setFunctionEnvs: async (projectId: string, functionId: string, env: Record<string, string>) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminUtilHandlerId}?ac=env-set&pid=${projectId}&fid=${functionId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ env }) });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { success: true };  
    } catch (err) {
      error = err;
    }
    return { error };
  },

  // setup default function environment variables
  setupDefaultFunctionEnvs: async (projectId: string, functionId: string) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminUtilHandlerId}?ac=env-default&pid=${projectId}&fid=${functionId}`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return { success: true };  
    } catch (err) {
      error = err;
    }
    return { error };
  },

  // AI
  getAIResponse: async (message: string) => {
    let error;
    try {
      const response = await fetch(`${FUNCTION_URL}/${adminUtilHandlerId}?ac=ai-response`, { method: 'POST', body: JSON.stringify({ message }), headers: { 'Content-Type': 'application/json' } });
      if (response.status !== 200) {
        return { error: await response.text() };
      }
      return await response.json();
    } catch (err) {
      error = err;
    }
    return { error };
  }
}